import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { type Editor } from '@tiptap/react';
import {
  CheckIcon,
  ChevronsUpDownIcon,
  PlusIcon,
  Trash2Icon
} from 'lucide-react';
import { useRef, useState } from 'react';
import type React from 'react';
import type { PropsWithChildren, ReactNode } from 'react';
import { toast } from 'sonner';

import Header from '../Header';
import { RightSidebarWrapper } from '../RightSidebar/RightSidebarWrapper';
import useOutletContext from '../useOutletContext';

import { OperatingPartnersTable } from './OperatingPartnersTable';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/Dialog';
import { EditButton } from '@/components/EditButton';
import FileInput from '@/components/FileInput';
import NumberField from '@/components/NumberField';
import RichTextField from '@/components/RichTextField';
import RichTextFieldReadOnly from '@/components/RichTextFieldReadOnly';
import { TruncatedTextTooltip } from '@/components/TruncatedTextTooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { Empty, EmptyContent } from '@/components/ui/empty';
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupText
} from '@/components/ui/input-group';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { useDebounce } from '@/hooks/useDebounce';
import { cn, fileToBase64 } from '@/lib/utils';

interface GetContacts {
  contacts: {
    edges: {
      node: {
        id: string;
        name: null | string;
      };
    }[];
  };
}

interface GetTalentMetrics {
  node: {
    __typename: 'OpportunityNode';
    seniorManagementContactIds: {
      __typename: 'ContactNode';
      displayName: null | string;
      email: null | string;
      function: null | string;
      id: string;
      image128: null | string;
      name: null | string;
      phone: null | string;
    }[];
    talentId: {
      __typename: 'TalentNode';
      assetsUnderManagement: null | number;
      boardCommentary: null | string;
      dealOpportunityId: {
        __typename: 'OpportunityNode';
        dealSponsor: { id: string; name: string } | null;
        dealSponsorType: null | string;
        id: string;
        name: null | string;
      };
      dryPowder: null | string;
      employeesFullTimeCount: null | number;
      employeesPartTimeCount: null | number;
      fundInvestingOutOf: null | string;
      hasReleventIndustryExperience: boolean | null;
      id: string;
      isUnionized: boolean | null;
      laborSupply: null | string;
      managementCompanyTenure: null | number;
      managementFdoPercentage: null | number;
      managementIndustryTenure: null | number;
      managementTeamSize: null | number;
      name: null | string;
      numberOfDirectorsWithEquity: null | number;
      numberOfIndependentDirectors: null | number;
      operatingPartnerIds?:
        | {
            id: string;
            name: null | string;
            position: null | string;
            relevantExperience: null | string;
          }[]
        | null;
      operatingPartners: null | string;
      operatingPartnersPositions: null | string;
      operatingPartnersReleventExperience: null | string;
      organizationalChart: null | string;
      principals: null | string;
      sizeOfBoard: null | number;
      sponsorCommentary: null | string;
      sponsorStrategy: null | string;
      sponsorType: null | string;
      turnoverPercentage: null | number;
    };
  };
}

interface SearchAccountsData {
  searchAccounts: {
    dynamicsId: null | string;
    fullRelationshipType: null | string;
    id: string;
    name: string;
    relationshipType: null | string;
  }[];
}

interface SelectionFieldsData {
  getFieldSelections: {
    id: string;
    name: string;
    value: string;
  }[];
}

const GET_LABOR_SUPPLY_SELECTION_FIELDS = gql`
  query SelectionFields {
    getFieldSelections(
      model: "opstream.company.talent"
      fieldName: "labor_supply"
    ) {
      id
      name
      value
    }
  }
`;

const SEARCH_ACCOUNTS = gql`
  query Accounts($company_name: [String], $selected_company: [String]) {
    searchAccounts(
      name: $company_name
      selectedAccounts: $selected_company
      limit: 100
    ) {
      id
      name
      dynamicsId
      relationshipType
      fullRelationshipType
    }
  }
`;

const UPDATE_OPPORTUNITY_SPONSOR = gql`
  mutation UpdateOpportunitySponsor($id: ID!, $dealSponsor: ID) {
    updateOpportunity(id: $id, values: { dealSponsor: $dealSponsor }) {
      ok
    }
  }
`;

const GET_CONTACTS = gql`
  query contacts($name: String) {
    contacts(isCompany: false, name: $name, first: 100) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

const GET_SPONSOR_TYPE_SELECTION_FIELDS = gql`
  query SponsorTypeSelectionFields {
    getFieldSelections(
      model: "opstream.company.talent"
      fieldName: "sponsor_type"
    ) {
      id
      name
      value
    }
  }
`;

const GET_TALENT_METRICS = gql`
  query GetTalentMetrics($id: ID!) {
    node(id: $id) {
      ... on OpportunityNode {
        seniorManagementContactIds {
          id
          displayName
          name
          email
          phone
          function
          image128
        }
        talentId {
          id
          name
          dealOpportunityId {
            id
            name
            dealSponsor {
              id
              name
            }
            dealSponsorType
          }
          managementTeamSize
          managementIndustryTenure
          managementCompanyTenure
          managementFdoPercentage
          employeesFullTimeCount
          employeesPartTimeCount
          turnoverPercentage
          laborSupply
          isUnionized
          sponsorType
          assetsUnderManagement
          sponsorStrategy
          principals
          fundInvestingOutOf
          dryPowder
          sponsorCommentary
          sizeOfBoard
          numberOfIndependentDirectors
          hasReleventIndustryExperience
          numberOfDirectorsWithEquity
          operatingPartnerIds {
            id
            name
            position
            relevantExperience
          }
          operatingPartners
          operatingPartnersPositions
          operatingPartnersReleventExperience
          boardCommentary
          organizationalChart
        }
      }
    }
  }
`;

const UPDATE_TALENT_METRICS = gql`
  mutation UpdateTalentMetrics(
    $id: ID!
    $management_team_size: Int
    $management_industry_tenure: Int
    $management_company_tenure: Int
    $management_fdo_percentage: Float
    $employees_full_time_count: Int
    $employees_part_time_count: Int
    $turnover_percentage: Float
    $labor_supply: String
    $is_unionized: Boolean
    $sponsor_type: String
    $assets_under_management: Int
    $sponsor_strategy: String
    $principals: String
    $fund_investing_out_of: String
    $dry_powder: String
    $sponsor_commentary: String
    $size_of_board: Int
    $number_of_independent_directors: Int
    $has_relevent_industry_experience: Boolean
    $number_of_directors_with_equity: Int
    $operating_partners: String
    $operating_partners_positions: String
    $operating_partners_relevent_experience: String
    $board_commentary: String
  ) {
    updateTalent(
      id: $id
      values: {
        managementTeamSize: $management_team_size
        managementIndustryTenure: $management_industry_tenure
        managementCompanyTenure: $management_company_tenure
        managementFdoPercentage: $management_fdo_percentage
        employeesFullTimeCount: $employees_full_time_count
        employeesPartTimeCount: $employees_part_time_count
        turnoverPercentage: $turnover_percentage
        laborSupply: $labor_supply
        isUnionized: $is_unionized
        sponsorType: $sponsor_type
        assetsUnderManagement: $assets_under_management
        sponsorStrategy: $sponsor_strategy
        principals: $principals
        fundInvestingOutOf: $fund_investing_out_of
        dryPowder: $dry_powder
        sponsorCommentary: $sponsor_commentary
        sizeOfBoard: $size_of_board
        numberOfIndependentDirectors: $number_of_independent_directors
        hasReleventIndustryExperience: $has_relevent_industry_experience
        numberOfDirectorsWithEquity: $number_of_directors_with_equity
        operatingPartners: $operating_partners
        operatingPartnersPositions: $operating_partners_positions
        operatingPartnersReleventExperience: $operating_partners_relevent_experience
        boardCommentary: $board_commentary
      }
    ) {
      ok
    }
  }
`;

const UPDATE_SENIOR_MANAGEMENT_CONTACTS = gql`
  mutation UpdateSeniorManagementContacts(
    $id: ID!
    $senior_management_contact_ids: [ID]
  ) {
    updateOpportunity(
      id: $id
      values: { seniorManagementContactIds: $senior_management_contact_ids }
    ) {
      ok
    }
  }
`;

const UPDATE_TALENT_IMAGES = gql`
  mutation UpdateTalentImages($id: ID!, $input: TalentInput!) {
    updateTalent(id: $id, values: $input) {
      ok
    }
  }
`;

const Card = ({ children }: PropsWithChildren) => (
  <div className="bg-card @container rounded-md border p-5">{children}</div>
);

const SectionHeader = ({
  actions,
  title
}: {
  actions?: ReactNode;
  title: string;
}) => (
  <div className="flex items-center justify-between gap-3">
    <h2 className="text-lg font-semibold">{title}</h2>
    {actions}
  </div>
);

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex min-w-0 items-center gap-3 text-sm">
    <div className="w-[300px] shrink-0">{label}</div>
    <TruncatedTextTooltip className="text-muted-foreground min-w-0 font-medium">
      {value}
    </TruncatedTextTooltip>
  </div>
);

const CommentaryField = ({
  label,
  value
}: {
  label: string;
  value: string;
}) => (
  <div className="space-y-2">
    <div className="text-sm font-medium">{label}</div>
    <RichTextFieldReadOnly html={value} />
  </div>
);

const InvestmentsDirectIdTalent = () => {
  const { id } = useOutletContext();
  const [addContactsSearch, setAddContactsSearch] = useState('');

  const {
    data: talentMetricsData,
    loading: loading1,
    refetch: refetchTalentMetrics
  } = useQuery<GetTalentMetrics, { id: string }>(GET_TALENT_METRICS, {
    nextFetchPolicy: 'no-cache',
    variables: { id }
  });

  const { data: laborSupplySelectionsData, loading: loading2 } =
    useQuery<SelectionFieldsData>(GET_LABOR_SUPPLY_SELECTION_FIELDS);

  const { data: sponsorTypeSelectionsData, loading: loading3 } =
    useQuery<SelectionFieldsData>(GET_SPONSOR_TYPE_SELECTION_FIELDS);

  const { data: contactsData } = useQuery<GetContacts>(GET_CONTACTS, {
    variables: {
      name: addContactsSearch.trim() || undefined
    }
  });

  const [updateSeniorManagementContacts, { loading: isUpdatingContacts }] =
    useMutation(UPDATE_SENIOR_MANAGEMENT_CONTACTS);

  const [updateOpportunitySponsor, { loading: isUpdatingOpportunitySponsor }] =
    useMutation(UPDATE_OPPORTUNITY_SPONSOR);

  const [updateTalentImages, { loading: isUpdatingTalentImages }] = useMutation(
    UPDATE_TALENT_IMAGES,
    {
      awaitRefetchQueries: true,
      refetchQueries: [{ query: GET_TALENT_METRICS, variables: { id } }]
    }
  );

  const [updateTalentMetrics, { loading: isUpdatingTalentMetrics }] =
    useMutation(UPDATE_TALENT_METRICS, {
      awaitRefetchQueries: true,
      refetchQueries: [{ query: GET_TALENT_METRICS, variables: { id } }]
    });

  const [activeTab, setActiveTab] = useState('company');
  const [companyDialogIsOpen, setCompanyDialogIsOpen] = useState(false);
  const [sponsorDialogIsOpen, setSponsorDialogIsOpen] = useState(false);
  const [boardDialogIsOpen, setBoardDialogIsOpen] = useState(false);

  const [addContactsDialogIsOpen, setAddContactsDialogIsOpen] = useState(false);
  const [addContactsComboboxIsOpen, setAddContactsComboboxIsOpen] =
    useState(false);
  const [selectedContact, setSelectedContact] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [contactToDelete, setContactToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const [laborSupplyComboboxIsOpen, setLaborSupplyComboboxIsOpen] =
    useState(false);
  const [laborSupplyValue, setLaborSupplyValue] = useState('');

  const [sponsorTypeComboboxIsOpen, setSponsorTypeComboboxIsOpen] =
    useState(false);
  const [sponsorTypeValue, setSponsorTypeValue] = useState('');

  const [sponsorCrmAccountComboboxIsOpen, setSponsorCrmAccountComboboxIsOpen] =
    useState(false);
  const [sponsorCrmAccountSearch, setSponsorCrmAccountSearch] = useState('');
  const debouncedSponsorSearch = useDebounce(sponsorCrmAccountSearch, 300);
  const [selectedSponsorCrmAccount, setSelectedSponsorCrmAccount] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const { data: accountsData, previousData: previousAccountsData } =
    useQuery<SearchAccountsData>(SEARCH_ACCOUNTS, {
      variables: {
        company_name: debouncedSponsorSearch.trim() || null,
        selected_company: selectedSponsorCrmAccount?.id
          ? [selectedSponsorCrmAccount.id]
          : null
      }
    });

  const accounts = (accountsData ?? previousAccountsData)?.searchAccounts ?? [];

  const sponsorCommentaryFieldRef = useRef<Editor>(null);
  const boardCommentaryFieldRef = useRef<Editor>(null);

  if ((!talentMetricsData && loading1) || loading2 || loading3) {
    return (
      <div className="-mt-16 grid h-[calc(100%+64px)] place-content-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  if (
    !talentMetricsData ||
    !laborSupplySelectionsData ||
    !sponsorTypeSelectionsData ||
    !accountsData
  ) {
    return <div>{'Error'}</div>;
  }

  const contactOptions = (contactsData?.contacts.edges ?? [])
    .map(edge => edge.node)
    .filter(node => node.name != null);

  const seniorManagementContacts =
    talentMetricsData.node.seniorManagementContactIds;
  const talentNodeId = talentMetricsData.node.talentId.id;

  const getString = (formData: FormData, key: string) => {
    const value = formData.get(key);
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  };

  const getInt = (formData: FormData, key: string) => {
    const value = getString(formData, key);
    if (!value) return null;
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const getFloat = (formData: FormData, key: string) => {
    const value = getString(formData, key);
    if (!value) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const getCheckbox = (formData: FormData, key: string) =>
    formData.get(key) === 'on';

  const handleAddContact = async () => {
    if (!selectedContact?.id) return;

    const nextIds = Array.from(
      new Set([selectedContact.id, ...seniorManagementContacts.map(c => c.id)])
    );

    try {
      const { error } = await updateSeniorManagementContacts({
        variables: {
          id,
          senior_management_contact_ids: nextIds
        }
      });

      if (error) {
        throw new Error('An error has occurred');
      }

      await refetchTalentMetrics({ id });

      setAddContactsDialogIsOpen(false);
      setSelectedContact(null);
      setAddContactsSearch('');
    } catch (error_) {
      const err = error_ as Error;
      console.error(err);
      toast.error(err.message);
    }
  };

  const handleDeleteContact = async (contactId: string) => {
    try {
      const { error } = await updateSeniorManagementContacts({
        variables: {
          id,
          senior_management_contact_ids: seniorManagementContacts
            .map(({ id }) => id)
            .filter(id => id !== contactId)
        }
      });

      if (error) {
        throw new Error('An error has occurred');
      }

      await refetchTalentMetrics({ id });

      setContactToDelete(null);
    } catch (error_) {
      const err = error_ as Error;
      console.error(err);
      toast.error(err.message);
    }
  };

  const handleCompanyAndManagementFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);

    try {
      const { error } = await updateTalentMetrics({
        variables: {
          employees_full_time_count: getInt(
            formData,
            'employees-full-time-count'
          ),
          employees_part_time_count: getInt(
            formData,
            'employees-part-time-count'
          ),
          id: talentNodeId,
          is_unionized: getCheckbox(formData, 'is-unionized'),
          labor_supply: getString(formData, 'labor-supply') ?? null,
          management_company_tenure: getInt(
            formData,
            'management-company-tenure'
          ),
          management_fdo_percentage: (() => {
            const v = getFloat(formData, 'management-fdo-percentage');
            return v != null ? v / 100 : null;
          })(),
          management_industry_tenure: getInt(
            formData,
            'management-industry-tenure'
          ),
          management_team_size: getInt(formData, 'management-team-size'),
          turnover_percentage: (() => {
            const v = getFloat(formData, 'turnover-percentage');
            return v != null ? v / 100 : null;
          })()
        }
      });

      if (error) {
        throw new Error('An error has occurred');
      }

      const organizationChartField = formData.get('organization-chart-field');
      const organizationChartBase64 =
        organizationChartField instanceof File &&
        organizationChartField.size > 0
          ? await fileToBase64(organizationChartField)
          : null;

      const { error: organizationChartError } = await updateTalentImages({
        variables: {
          id: talentNodeId,
          input: { organizationalChart: organizationChartBase64 }
        }
      });

      if (organizationChartError) {
        throw new Error('An error has occurred');
      }

      await refetchTalentMetrics();
      setCompanyDialogIsOpen(false);
    } catch (error_) {
      const err = error_ as Error;
      console.error(err);
      toast.error(err.message);
    }
  };

  const handleSponsorFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);

    if (!sponsorCommentaryFieldRef.current) {
      throw new Error();
    }

    const sponsorCrmAccountId = getString(formData, 'sponsor-crm-account');

    if (
      sponsorCrmAccountId !==
      talentMetricsData.node.talentId.dealOpportunityId.dealSponsor?.id
    ) {
      try {
        const { error } = await updateOpportunitySponsor({
          variables: {
            dealSponsor: sponsorCrmAccountId,
            id: talentMetricsData.node.talentId.dealOpportunityId.id
          }
        });

        if (error) {
          throw new Error('Failed to update sponsor');
        }
      } catch (error) {
        console.error(error);
        toast.error('Failed to update sponsor');
        return;
      }
    }

    try {
      const { error } = await updateTalentMetrics({
        variables: {
          assets_under_management: getInt(formData, 'assets-under-management'),
          dry_powder: getFloat(formData, 'dry-powder')?.toString() ?? null,
          fund_investing_out_of:
            getString(formData, 'fund-investing-out-of') ?? null,
          id: talentNodeId,
          principals: getString(formData, 'principals')?.toString() ?? null,
          sponsor_commentary: sponsorCommentaryFieldRef.current.isEmpty
            ? null
            : sponsorCommentaryFieldRef.current.getHTML(),
          sponsor_strategy:
            getInt(formData, 'sponsor-strategy')?.toString() ?? null,
          sponsor_type: getString(formData, 'sponsor-type') ?? null
        }
      });

      if (error) {
        throw new Error('An error has occurred');
      }

      await refetchTalentMetrics();
      setSponsorDialogIsOpen(false);
    } catch (error_) {
      const err = error_ as Error;
      console.error(err);
      toast.error(err.message);
    }
  };

  const handleBoardAndOperatingPartnersFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);

    if (!boardCommentaryFieldRef.current) {
      throw new Error();
    }

    try {
      const { error } = await updateTalentMetrics({
        variables: {
          board_commentary: boardCommentaryFieldRef.current.isEmpty
            ? null
            : boardCommentaryFieldRef.current.getHTML(),
          has_relevent_industry_experience: (() => {
            const v = getString(formData, 'relevant-industry-experience');
            if (v === 'true') return true;
            if (v === 'false') return false;
            return null;
          })(),
          id: talentNodeId,
          number_of_directors_with_equity: getInt(
            formData,
            'number-of-directors-with-equity'
          ),
          number_of_independent_directors: getInt(
            formData,
            'number-of-independent-directors'
          ),
          size_of_board: getInt(formData, 'size-of-board')
        }
      });

      if (error) {
        throw new Error('An error has occurred');
      }

      await refetchTalentMetrics();
      setBoardDialogIsOpen(false);
    } catch (error_) {
      const err = error_ as Error;
      console.error(err);
      toast.error(err.message);
    }
  };

  const laborSupplyOptions = laborSupplySelectionsData.getFieldSelections.map(
    option => ({
      label: option.name,
      value: option.value
    })
  );

  const sponsorTypeOptions = sponsorTypeSelectionsData.getFieldSelections.map(
    option => ({
      label: option.name,
      value: option.value
    })
  );

  return (
    <>
      <Header />
      <RightSidebarWrapper>
        <div className="min-w-0 space-y-5 px-5 pt-5 pb-16.5 md:pl-14">
          <Tabs
            className="gap-5"
            onValueChange={setActiveTab}
            value={activeTab}
          >
            <TabsList className="h-auto! w-full justify-start overflow-x-auto overflow-y-hidden">
              <TabsTrigger
                className="flex-1"
                value="company"
              >
                Company & Management
              </TabsTrigger>
              <TabsTrigger
                className="flex-1"
                value="sponsor"
              >
                Sponsor
              </TabsTrigger>
              <TabsTrigger
                className="flex-1"
                value="board"
              >
                Board & Operating Partners
              </TabsTrigger>
            </TabsList>
            <TabsContent
              className="space-y-2.5"
              value="company"
            >
              <SectionHeader
                actions={
                  <Dialog
                    onOpenChange={nextOpen => {
                      setCompanyDialogIsOpen(nextOpen);
                      if (nextOpen) {
                        setLaborSupplyValue(
                          talentMetricsData.node.talentId.laborSupply ?? ''
                        );
                      }
                    }}
                    open={companyDialogIsOpen}
                  >
                    <DialogTrigger asChild>
                      <EditButton />
                    </DialogTrigger>
                    <DialogContent aria-describedby={undefined}>
                      <DialogHeader>
                        <DialogTitle>
                          Company & Management
                          <Badge variant="yellow">Unsaved</Badge>
                        </DialogTitle>
                      </DialogHeader>
                      <DialogBody>
                        <form
                          id="company-and-management-form"
                          noValidate
                          onSubmit={event =>
                            void handleCompanyAndManagementFormSubmit(event)
                          }
                        >
                          <FieldGroup>
                            <FieldSet>
                              <FieldLegend>Senior Management</FieldLegend>
                              <FieldGroup>
                                <div className="grid grid-cols-2 gap-x-2.5">
                                  <Field>
                                    <FieldLabel htmlFor="management-team-size">
                                      Size of Senior Team
                                    </FieldLabel>
                                    <NumberField
                                      defaultValue={
                                        talentMetricsData.node.talentId
                                          .managementTeamSize ?? undefined
                                      }
                                      id="management-team-size"
                                      name="management-team-size"
                                      placeholder="Enter Value"
                                    />
                                  </Field>
                                  <Field>
                                    <FieldLabel htmlFor="management-company-tenure">
                                      Average Company Tenure
                                    </FieldLabel>
                                    <NumberField
                                      defaultValue={
                                        talentMetricsData.node.talentId
                                          .managementCompanyTenure ?? undefined
                                      }
                                      id="management-company-tenure"
                                      name="management-company-tenure"
                                      placeholder="Enter Value"
                                    />
                                  </Field>
                                </div>
                                <div className="grid grid-cols-2 gap-x-2.5">
                                  <Field>
                                    <FieldLabel htmlFor="management-fdo-percentage">
                                      Management FDO %
                                    </FieldLabel>
                                    <InputGroup>
                                      <NumberField
                                        defaultValue={(() => {
                                          const v =
                                            talentMetricsData.node.talentId
                                              .managementFdoPercentage;
                                          if (v == null) return undefined;
                                          return v > 1 ? v : v * 100;
                                        })()}
                                        id="management-fdo-percentage"
                                        max={100}
                                        name="management-fdo-percentage"
                                        placeholder="Enter Value"
                                      />
                                      <InputGroupAddon align="inline-end">
                                        <InputGroupText>%</InputGroupText>
                                      </InputGroupAddon>
                                    </InputGroup>
                                  </Field>
                                  <Field>
                                    <FieldLabel htmlFor="management-industry-tenure">
                                      Average Industry Tenure
                                    </FieldLabel>
                                    <NumberField
                                      defaultValue={
                                        talentMetricsData.node.talentId
                                          .managementIndustryTenure ?? undefined
                                      }
                                      id="management-industry-tenure"
                                      name="management-industry-tenure"
                                      placeholder="Enter Value"
                                    />
                                  </Field>
                                </div>
                                <div className="grid grid-cols-2 gap-x-2.5">
                                  <Field>
                                    <FieldLabel htmlFor="organization-chart-field">
                                      Organization Chart
                                    </FieldLabel>
                                    <FileInput
                                      accept="image/*"
                                      data={
                                        talentMetricsData.node.talentId
                                          .organizationalChart
                                      }
                                      id="organization-chart-field"
                                      name="organization-chart-field"
                                    />
                                  </Field>
                                </div>
                              </FieldGroup>
                            </FieldSet>
                            <FieldSet>
                              <FieldLegend>Employees</FieldLegend>
                              <FieldGroup>
                                <div className="grid grid-cols-2 gap-x-2.5">
                                  <Field>
                                    <FieldLabel htmlFor="employees-full-time-count">
                                      Total FTE Count
                                    </FieldLabel>
                                    <NumberField
                                      defaultValue={
                                        talentMetricsData.node.talentId
                                          .employeesFullTimeCount ?? undefined
                                      }
                                      id="employees-full-time-count"
                                      name="employees-full-time-count"
                                      placeholder="Enter Value"
                                    />
                                  </Field>
                                  <Field>
                                    <FieldLabel htmlFor="employees-part-time-count">
                                      Total Part-Time Count
                                    </FieldLabel>
                                    <NumberField
                                      defaultValue={
                                        talentMetricsData.node.talentId
                                          .employeesPartTimeCount ?? undefined
                                      }
                                      id="employees-part-time-count"
                                      name="employees-part-time-count"
                                      placeholder="Enter Value"
                                    />
                                  </Field>
                                </div>
                                <div className="grid grid-cols-2 gap-x-2.5">
                                  <Field>
                                    <FieldLabel htmlFor="turnover-percentage">
                                      Turnover Percentage
                                    </FieldLabel>
                                    <InputGroup>
                                      <NumberField
                                        defaultValue={(() => {
                                          const v =
                                            talentMetricsData.node.talentId
                                              .turnoverPercentage;
                                          if (v == null) return undefined;
                                          return v > 1 ? v : v * 100;
                                        })()}
                                        id="turnover-percentage"
                                        max={100}
                                        name="turnover-percentage"
                                        placeholder="Enter Value"
                                      />
                                      <InputGroupAddon align="inline-end">
                                        <InputGroupText>%</InputGroupText>
                                      </InputGroupAddon>
                                    </InputGroup>
                                  </Field>
                                  <Field>
                                    <FieldLabel htmlFor="is-unionized">
                                      Unionized
                                    </FieldLabel>
                                    <div className="flex h-9 items-center">
                                      <Checkbox
                                        defaultChecked={
                                          talentMetricsData.node.talentId
                                            .isUnionized ?? false
                                        }
                                        id="is-unionized"
                                        name="is-unionized"
                                      />
                                    </div>
                                  </Field>
                                </div>
                                <Field>
                                  <FieldLabel htmlFor="labor-supply">
                                    Labor Supply
                                  </FieldLabel>
                                  <Popover
                                    modal={true}
                                    onOpenChange={setLaborSupplyComboboxIsOpen}
                                    open={laborSupplyComboboxIsOpen}
                                  >
                                    <PopoverTrigger asChild>
                                      <Button
                                        aria-expanded={
                                          laborSupplyComboboxIsOpen
                                        }
                                        className="w-full justify-between"
                                        id="labor-supply"
                                        role="combobox"
                                        variant="outline"
                                      >
                                        {laborSupplyValue
                                          ? laborSupplyOptions.find(
                                              option =>
                                                option.value ===
                                                laborSupplyValue
                                            )?.label
                                          : 'Select an option'}
                                        <ChevronsUpDownIcon className="opacity-50" />
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
                                      <Command>
                                        <CommandInput
                                          className="h-9"
                                          placeholder="Search labor supply..."
                                        />
                                        <CommandList>
                                          <CommandEmpty>
                                            {laborSupplyOptions.length === 0
                                              ? 'No options'
                                              : 'No option found.'}
                                          </CommandEmpty>
                                          <CommandGroup>
                                            {laborSupplyOptions.map(option => (
                                              <CommandItem
                                                key={option.value}
                                                onSelect={() => {
                                                  setLaborSupplyValue(
                                                    current =>
                                                      current === option.value
                                                        ? ''
                                                        : option.value
                                                  );
                                                  setLaborSupplyComboboxIsOpen(
                                                    false
                                                  );
                                                }}
                                                value={option.label}
                                              >
                                                {option.label}
                                                <CheckIcon
                                                  className={cn(
                                                    'ml-auto',
                                                    laborSupplyValue ===
                                                      option.value
                                                      ? 'opacity-100'
                                                      : 'opacity-0'
                                                  )}
                                                />
                                              </CommandItem>
                                            ))}
                                          </CommandGroup>
                                        </CommandList>
                                      </Command>
                                    </PopoverContent>
                                  </Popover>
                                  <Input
                                    name="labor-supply"
                                    type="hidden"
                                    value={laborSupplyValue}
                                  />
                                </Field>
                              </FieldGroup>
                            </FieldSet>
                          </FieldGroup>
                        </form>
                      </DialogBody>
                      <DialogFooter>
                        <Button
                          disabled={
                            isUpdatingTalentMetrics || isUpdatingTalentImages
                          }
                          form="company-and-management-form"
                          type="submit"
                        >
                          {(isUpdatingTalentMetrics ||
                            isUpdatingTalentImages) && <Spinner />}
                          Save
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                }
                title="Company & Management"
              />
              <Card>
                <div className="space-y-5">
                  <div className="border-b pb-2.5">
                    <h3 className="text-base font-medium">Senior Management</h3>
                  </div>
                  <div className="grid gap-4 @md:grid-cols-2">
                    <InfoRow
                      label="Size of Senior Team"
                      value={
                        talentMetricsData.node.talentId.managementTeamSize?.toString() ??
                        '-'
                      }
                    />
                    <InfoRow
                      label="Average Company Tenure"
                      value={
                        talentMetricsData.node.talentId.managementCompanyTenure?.toString() ??
                        '-'
                      }
                    />
                  </div>
                  <div className="grid gap-4 @md:grid-cols-2">
                    <InfoRow
                      label="Average Industry Tenure"
                      value={
                        talentMetricsData.node.talentId.managementIndustryTenure?.toString() ??
                        '-'
                      }
                    />
                    <InfoRow
                      label="Management FDO %"
                      value={(() => {
                        const v =
                          talentMetricsData.node.talentId
                            .managementFdoPercentage;
                        if (v == null) return '-';
                        const display = v > 1 ? v : v * 100;
                        return `${display.toFixed(1)}%`;
                      })()}
                    />
                  </div>
                  <div className="grid items-center gap-4 md:grid-cols-[300px_1fr]">
                    <div className="text-sm">Organization Chart</div>
                    <FileInput
                      accept="image/*"
                      data={talentMetricsData.node.talentId.organizationalChart}
                      readOnly
                    />
                  </div>
                  <div className="border-b pb-2.5">
                    <h3 className="text-base font-medium">Employees</h3>
                  </div>
                  <div className="grid gap-4 @md:grid-cols-2">
                    <InfoRow
                      label="Total FTE Count"
                      value={
                        talentMetricsData.node.talentId.employeesFullTimeCount?.toString() ??
                        '-'
                      }
                    />
                    <InfoRow
                      label="Total Part-Time Count"
                      value={
                        talentMetricsData.node.talentId.employeesPartTimeCount?.toString() ??
                        '-'
                      }
                    />
                  </div>
                  <div className="grid gap-4 @md:grid-cols-2">
                    <InfoRow
                      label="Turnover Percentage"
                      value={(() => {
                        const v =
                          talentMetricsData.node.talentId.turnoverPercentage;
                        if (v == null) return '-';
                        const display = v > 1 ? v : v * 100;
                        return `${display.toFixed(1)}%`;
                      })()}
                    />
                    <div className="flex min-w-0 items-center gap-3 text-sm">
                      <div className="w-[300px] shrink-0">Unionized</div>
                      <Checkbox
                        checked={
                          talentMetricsData.node.talentId.isUnionized ?? false
                        }
                        disabled
                      />
                    </div>
                  </div>
                  <div className="grid gap-4 @md:grid-cols-2">
                    <InfoRow
                      label="Labor Supply"
                      value={talentMetricsData.node.talentId.laborSupply ?? '-'}
                    />
                  </div>
                </div>
              </Card>
              <SectionHeader
                actions={
                  <Dialog
                    onOpenChange={open => {
                      setAddContactsDialogIsOpen(open);
                      if (!open) {
                        setSelectedContact(null);
                        setAddContactsSearch('');
                        setAddContactsComboboxIsOpen(false);
                      }
                    }}
                    open={addContactsDialogIsOpen}
                  >
                    {talentMetricsData.node.seniorManagementContactIds
                      .length !== 0 && (
                      <DialogTrigger asChild>
                        <Button type="button">
                          <PlusIcon />
                          Add Contacts
                        </Button>
                      </DialogTrigger>
                    )}
                    <DialogContent aria-describedby={undefined}>
                      <DialogHeader>
                        <DialogTitle>Add Contacts</DialogTitle>
                      </DialogHeader>

                      <DialogBody>
                        <FieldGroup>
                          <Field>
                            <FieldLabel htmlFor="add-contacts-combobox">
                              Add Contacts
                            </FieldLabel>
                            <Popover
                              modal={true}
                              onOpenChange={setAddContactsComboboxIsOpen}
                              open={addContactsComboboxIsOpen}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  aria-expanded={addContactsComboboxIsOpen}
                                  className="w-full justify-between"
                                  id="add-contacts-combobox"
                                  role="combobox"
                                  type="button"
                                  variant="outline"
                                >
                                  {selectedContact?.name ?? 'Select a contact'}
                                  <ChevronsUpDownIcon className="opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
                                <Command shouldFilter={false}>
                                  <CommandInput
                                    className="h-9"
                                    onValueChange={setAddContactsSearch}
                                    placeholder="Search contacts..."
                                    value={addContactsSearch}
                                  />
                                  <CommandList>
                                    <CommandEmpty>
                                      No contacts found.
                                    </CommandEmpty>
                                    <CommandGroup>
                                      {contactOptions.map(option => (
                                        <CommandItem
                                          key={option.id}
                                          onSelect={() => {
                                            setSelectedContact({
                                              id: option.id,
                                              name: option.name ?? ''
                                            });
                                            setAddContactsComboboxIsOpen(false);
                                          }}
                                          value={option.name ?? ''}
                                        >
                                          {option.name}
                                          <CheckIcon
                                            className={cn(
                                              'ml-auto',
                                              selectedContact?.id === option.id
                                                ? 'opacity-100'
                                                : 'opacity-0'
                                            )}
                                          />
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </Field>
                        </FieldGroup>
                      </DialogBody>

                      <DialogFooter>
                        <Button
                          disabled={!selectedContact || isUpdatingContacts}
                          onClick={() => {
                            void handleAddContact();
                          }}
                          type="button"
                        >
                          {isUpdatingContacts && <Spinner />}
                          Add
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                }
                title="Senior Management Contacts"
              />
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs font-semibold">
                        Name
                      </TableHead>
                      <TableHead className="text-xs font-semibold">
                        Function
                      </TableHead>
                      <TableHead className="text-xs font-semibold">
                        Email
                      </TableHead>
                      <TableHead className="text-xs font-semibold">
                        Business Phone Number
                      </TableHead>
                      <TableHead className="w-0" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {talentMetricsData.node.seniorManagementContactIds
                      .length === 0 ? (
                      <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={4}>
                          <Empty className="px-0! pt-5! pb-0!">
                            <EmptyContent>
                              <Button
                                onClick={() => {
                                  setAddContactsDialogIsOpen(true);
                                }}
                                type="button"
                              >
                                <PlusIcon />
                                Add Contacts
                              </Button>
                            </EmptyContent>
                          </Empty>
                        </TableCell>
                      </TableRow>
                    ) : (
                      talentMetricsData.node.seniorManagementContactIds.map(
                        contact => (
                          <TableRow key={contact.id}>
                            <TableCell className="font-medium">
                              {contact.displayName ?? contact.name ?? '-'}
                            </TableCell>
                            <TableCell>{contact.function ?? '-'}</TableCell>
                            <TableCell>{contact.email ?? '-'}</TableCell>
                            <TableCell>{contact.phone ?? '-'}</TableCell>
                            <TableCell className="w-0">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    onClick={() => {
                                      setContactToDelete({
                                        id: contact.id,
                                        name:
                                          contact.displayName ??
                                          contact.name ??
                                          ''
                                      });
                                    }}
                                    size="icon-sm"
                                    variant="ghost"
                                  >
                                    <Trash2Icon />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Delete Contact</TooltipContent>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        )
                      )
                    )}
                  </TableBody>
                </Table>
              </Card>
              <AlertDialog
                onOpenChange={open => {
                  if (!open) {
                    setContactToDelete(null);
                  }
                }}
                open={contactToDelete !== null}
              >
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete contact</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete{' '}
                      <strong>{contactToDelete?.name ?? 'this contact'}</strong>
                      ? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      disabled={isUpdatingContacts}
                      onClick={e => {
                        e.preventDefault();
                        if (contactToDelete) {
                          void handleDeleteContact(contactToDelete.id);
                        }
                      }}
                      variant="destructive"
                    >
                      {isUpdatingContacts && <Spinner />}
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </TabsContent>
            <TabsContent
              className="space-y-2.5"
              value="sponsor"
            >
              <SectionHeader
                actions={
                  <Dialog
                    onOpenChange={nextOpen => {
                      setSponsorDialogIsOpen(nextOpen);
                      if (nextOpen) {
                        setSponsorTypeValue(
                          talentMetricsData.node.talentId.sponsorType ?? ''
                        );
                        const currentSponsor =
                          talentMetricsData.node.talentId.dealOpportunityId
                            .dealSponsor;
                        setSelectedSponsorCrmAccount(
                          currentSponsor
                            ? {
                                id: currentSponsor.id,
                                name: currentSponsor.name
                              }
                            : null
                        );
                      }
                    }}
                    open={sponsorDialogIsOpen}
                  >
                    <DialogTrigger asChild>
                      <EditButton />
                    </DialogTrigger>
                    <DialogContent aria-describedby={undefined}>
                      <DialogHeader>
                        <DialogTitle>
                          Sponsor
                          <Badge variant="yellow">Unsaved</Badge>
                        </DialogTitle>
                      </DialogHeader>
                      <DialogBody>
                        <form
                          id="sponsor-form"
                          noValidate
                          onSubmit={event =>
                            void handleSponsorFormSubmit(event)
                          }
                        >
                          <FieldGroup>
                            <FieldSet>
                              <FieldLegend>General</FieldLegend>
                              <FieldGroup>
                                <div className="grid grid-cols-2 gap-x-2.5">
                                  <Field>
                                    <FieldLabel htmlFor="sponsor-crm-account">
                                      Sponsor (CRM Account)
                                    </FieldLabel>
                                    <Popover
                                      modal={true}
                                      onOpenChange={
                                        setSponsorCrmAccountComboboxIsOpen
                                      }
                                      open={sponsorCrmAccountComboboxIsOpen}
                                    >
                                      <PopoverTrigger asChild>
                                        <Button
                                          aria-expanded={
                                            sponsorCrmAccountComboboxIsOpen
                                          }
                                          className="w-full justify-between"
                                          id="sponsor-crm-account"
                                          role="combobox"
                                          variant="outline"
                                        >
                                          {selectedSponsorCrmAccount
                                            ? selectedSponsorCrmAccount.name
                                            : 'Select an option'}
                                          <ChevronsUpDownIcon className="opacity-50" />
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
                                        <Command shouldFilter={false}>
                                          <CommandInput
                                            className="h-9"
                                            onValueChange={
                                              setSponsorCrmAccountSearch
                                            }
                                            placeholder="Search sponsor..."
                                            value={sponsorCrmAccountSearch}
                                          />
                                          <CommandList>
                                            <CommandEmpty>
                                              No sponsor found.
                                            </CommandEmpty>
                                            <CommandGroup>
                                              {accounts.map(account => (
                                                <CommandItem
                                                  key={account.id}
                                                  onSelect={() => {
                                                    setSelectedSponsorCrmAccount(
                                                      {
                                                        id: account.id,
                                                        name: account.name
                                                      }
                                                    );
                                                    setSponsorCrmAccountComboboxIsOpen(
                                                      false
                                                    );
                                                  }}
                                                  value={account.name}
                                                >
                                                  {account.name}
                                                  <CheckIcon
                                                    className={cn(
                                                      'ml-auto',
                                                      selectedSponsorCrmAccount?.id ===
                                                        account.id
                                                        ? 'opacity-100'
                                                        : 'opacity-0'
                                                    )}
                                                  />
                                                </CommandItem>
                                              ))}
                                            </CommandGroup>
                                          </CommandList>
                                        </Command>
                                      </PopoverContent>
                                    </Popover>
                                    <Input
                                      name="sponsor-crm-account"
                                      type="hidden"
                                      value={
                                        selectedSponsorCrmAccount?.id ?? ''
                                      }
                                    />
                                  </Field>
                                  <Field>
                                    <FieldLabel htmlFor="sponsor-type">
                                      Sponsor Type
                                    </FieldLabel>
                                    <Popover
                                      modal={true}
                                      onOpenChange={
                                        setSponsorTypeComboboxIsOpen
                                      }
                                      open={sponsorTypeComboboxIsOpen}
                                    >
                                      <PopoverTrigger asChild>
                                        <Button
                                          aria-expanded={
                                            sponsorTypeComboboxIsOpen
                                          }
                                          className="w-full justify-between"
                                          id="sponsor-type"
                                          role="combobox"
                                          variant="outline"
                                        >
                                          {sponsorTypeValue
                                            ? sponsorTypeOptions.find(
                                                option =>
                                                  option.value ===
                                                  sponsorTypeValue
                                              )?.label
                                            : 'Select an option'}
                                          <ChevronsUpDownIcon className="opacity-50" />
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
                                        <Command>
                                          <CommandInput
                                            className="h-9"
                                            placeholder="Search sponsor type..."
                                          />
                                          <CommandList>
                                            <CommandEmpty>
                                              {sponsorTypeOptions.length === 0
                                                ? 'No options'
                                                : 'No option found.'}
                                            </CommandEmpty>
                                            <CommandGroup>
                                              {sponsorTypeOptions.map(
                                                option => (
                                                  <CommandItem
                                                    key={option.value}
                                                    onSelect={() => {
                                                      setSponsorTypeValue(
                                                        current =>
                                                          current ===
                                                          option.value
                                                            ? ''
                                                            : option.value
                                                      );
                                                      setSponsorTypeComboboxIsOpen(
                                                        false
                                                      );
                                                    }}
                                                    value={option.label}
                                                  >
                                                    {option.label}
                                                    <CheckIcon
                                                      className={cn(
                                                        'ml-auto',
                                                        sponsorTypeValue ===
                                                          option.value
                                                          ? 'opacity-100'
                                                          : 'opacity-0'
                                                      )}
                                                    />
                                                  </CommandItem>
                                                )
                                              )}
                                            </CommandGroup>
                                          </CommandList>
                                        </Command>
                                      </PopoverContent>
                                    </Popover>
                                    <Input
                                      name="sponsor-type"
                                      type="hidden"
                                      value={sponsorTypeValue}
                                    />
                                  </Field>
                                </div>
                                <div className="grid grid-cols-2 gap-x-2.5">
                                  <Field>
                                    <FieldLabel htmlFor="fund-investing-out-of">
                                      Fund Investing Out Of
                                    </FieldLabel>
                                    <Input
                                      defaultValue={
                                        talentMetricsData.node.talentId
                                          .fundInvestingOutOf ?? ''
                                      }
                                      id="fund-investing-out-of"
                                      name="fund-investing-out-of"
                                      placeholder="Enter Value"
                                    />
                                  </Field>
                                  <Field>
                                    <FieldLabel htmlFor="sponsor-strategy">
                                      Typical Investment Focus
                                    </FieldLabel>
                                    <Input
                                      defaultValue={
                                        talentMetricsData.node.talentId
                                          .sponsorStrategy ?? ''
                                      }
                                      id="sponsor-strategy"
                                      name="sponsor-strategy"
                                      placeholder="Enter Value"
                                    />
                                  </Field>
                                </div>
                                <div className="grid grid-cols-2 gap-x-2.5">
                                  <Field>
                                    <FieldLabel htmlFor="assets-under-management">
                                      Assets Under Management
                                    </FieldLabel>
                                    <InputGroup>
                                      <InputGroupAddon>
                                        <InputGroupText>$</InputGroupText>
                                      </InputGroupAddon>
                                      <NumberField
                                        defaultValue={
                                          talentMetricsData.node.talentId
                                            .assetsUnderManagement ?? undefined
                                        }
                                        id="assets-under-management"
                                        name="assets-under-management"
                                        placeholder="Enter Value"
                                      />
                                    </InputGroup>
                                  </Field>
                                  <Field>
                                    <FieldLabel htmlFor="dry-powder">
                                      Dry Powder
                                    </FieldLabel>
                                    <NumberField
                                      defaultValue={
                                        talentMetricsData.node.talentId
                                          .dryPowder ?? ''
                                      }
                                      id="dry-powder"
                                      name="dry-powder"
                                      placeholder="Enter Value"
                                    />
                                  </Field>
                                </div>
                                <div className="grid grid-cols-2 gap-x-2.5">
                                  <Field>
                                    <FieldLabel htmlFor="principals">
                                      Principals
                                    </FieldLabel>
                                    <Input
                                      defaultValue={
                                        talentMetricsData.node.talentId
                                          .principals ?? ''
                                      }
                                      id="principals"
                                      name="principals"
                                      placeholder="Enter Value"
                                    />
                                  </Field>
                                </div>
                              </FieldGroup>
                            </FieldSet>
                            <Field>
                              <FieldLabel htmlFor="sponsor-commentary-field">
                                Sponsor Commentary
                              </FieldLabel>
                              <RichTextField
                                defaultValue={
                                  talentMetricsData.node.talentId
                                    .sponsorCommentary ?? ''
                                }
                                id="sponsor-commentary-field"
                                ref={sponsorCommentaryFieldRef}
                              />
                            </Field>
                          </FieldGroup>
                        </form>
                      </DialogBody>
                      <DialogFooter>
                        <Button
                          disabled={
                            isUpdatingTalentMetrics ||
                            isUpdatingOpportunitySponsor
                          }
                          form="sponsor-form"
                          type="submit"
                        >
                          {(isUpdatingTalentMetrics ||
                            isUpdatingOpportunitySponsor) && <Spinner />}
                          Save
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                }
                title="Sponsor"
              />
              <Card>
                <div className="space-y-5">
                  <div className="border-b pb-2.5">
                    <h3 className="text-base font-medium">General</h3>
                  </div>
                  <div className="grid gap-4 @md:grid-cols-2">
                    <InfoRow
                      label="Sponsor (CRM Account)"
                      value={
                        talentMetricsData.node.talentId.dealOpportunityId
                          .dealSponsor?.name ?? '-'
                      }
                    />
                    <InfoRow
                      label="Sponsor Type"
                      value={talentMetricsData.node.talentId.sponsorType ?? '-'}
                    />
                  </div>
                  <div className="grid gap-4 @md:grid-cols-2">
                    <InfoRow
                      label="Fund Investing Out Of"
                      value={
                        talentMetricsData.node.talentId.fundInvestingOutOf ??
                        '-'
                      }
                    />
                    <InfoRow
                      label="Typical Investment Focus"
                      value={
                        talentMetricsData.node.talentId.sponsorStrategy ?? '-'
                      }
                    />
                  </div>
                  <div className="grid gap-4 @md:grid-cols-2">
                    <InfoRow
                      label="Assets Under Management"
                      value={
                        talentMetricsData.node.talentId.assetsUnderManagement?.toString() ??
                        '-'
                      }
                    />
                    <InfoRow
                      label="Dry Powder"
                      value={talentMetricsData.node.talentId.dryPowder ?? '-'}
                    />
                  </div>
                  <div className="grid gap-4 @md:grid-cols-2">
                    <InfoRow
                      label="Principals"
                      value={talentMetricsData.node.talentId.principals ?? '-'}
                    />
                  </div>
                  <CommentaryField
                    label="Sponsor Commentary"
                    value={
                      talentMetricsData.node.talentId.sponsorCommentary ?? ''
                    }
                  />
                </div>
              </Card>
            </TabsContent>
            <TabsContent
              className="space-y-2.5"
              value="board"
            >
              <SectionHeader
                actions={
                  <Dialog
                    onOpenChange={setBoardDialogIsOpen}
                    open={boardDialogIsOpen}
                  >
                    <DialogTrigger asChild>
                      <EditButton />
                    </DialogTrigger>
                    <DialogContent aria-describedby={undefined}>
                      <DialogHeader>
                        <DialogTitle>
                          Board & Operating Partners
                          <Badge variant="yellow">Unsaved</Badge>
                        </DialogTitle>
                      </DialogHeader>
                      <DialogBody>
                        <form
                          id="board-and-operating-partners-form"
                          noValidate
                          onSubmit={event =>
                            void handleBoardAndOperatingPartnersFormSubmit(
                              event
                            )
                          }
                        >
                          <FieldGroup>
                            <FieldSet>
                              <FieldLegend>Board of Directors</FieldLegend>
                              <FieldGroup>
                                <div className="grid grid-cols-2 gap-x-2.5">
                                  <Field>
                                    <FieldLabel htmlFor="size-of-board">
                                      Size of Board
                                    </FieldLabel>
                                    <NumberField
                                      defaultValue={
                                        talentMetricsData.node.talentId
                                          .sizeOfBoard ?? undefined
                                      }
                                      id="size-of-board"
                                      name="size-of-board"
                                      placeholder="Enter Value"
                                    />
                                  </Field>
                                  <Field>
                                    <FieldLabel htmlFor="number-of-independent-directors">
                                      # of Independent Directors
                                    </FieldLabel>
                                    <NumberField
                                      defaultValue={
                                        talentMetricsData.node.talentId
                                          .numberOfIndependentDirectors ??
                                        undefined
                                      }
                                      id="number-of-independent-directors"
                                      name="number-of-independent-directors"
                                      placeholder="Enter Value"
                                    />
                                  </Field>
                                </div>
                                <div className="grid grid-cols-2 gap-x-2.5">
                                  <Field>
                                    <FieldLabel htmlFor="relevant-industry-experience">
                                      Relevant Industry Experience
                                    </FieldLabel>
                                    <Select
                                      defaultValue={
                                        talentMetricsData.node.talentId
                                          .hasReleventIndustryExperience ===
                                        null
                                          ? 'na'
                                          : talentMetricsData.node.talentId
                                                .hasReleventIndustryExperience
                                            ? 'true'
                                            : 'false'
                                      }
                                      name="relevant-industry-experience"
                                    >
                                      <SelectTrigger
                                        className="w-full"
                                        id="relevant-industry-experience"
                                      >
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="na">N/A</SelectItem>
                                        <SelectItem value="true">Yes</SelectItem>
                                        <SelectItem value="false">No</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </Field>
                                  <Field>
                                    <FieldLabel htmlFor="number-of-directors-with-equity">
                                      # of Directors with Equity
                                    </FieldLabel>
                                    <NumberField
                                      defaultValue={
                                        talentMetricsData.node.talentId
                                          .numberOfDirectorsWithEquity ??
                                        undefined
                                      }
                                      id="number-of-directors-with-equity"
                                      name="number-of-directors-with-equity"
                                      placeholder="Enter Value"
                                    />
                                  </Field>
                                </div>
                              </FieldGroup>
                            </FieldSet>
                            <FieldSet>
                              <FieldGroup>
                                <Field>
                                  <FieldLabel htmlFor="board-commentary-field">
                                    Commentary
                                  </FieldLabel>
                                  <RichTextField
                                    defaultValue={
                                      talentMetricsData.node.talentId
                                        .boardCommentary ?? ''
                                    }
                                    id="board-commentary-field"
                                    ref={boardCommentaryFieldRef}
                                  />
                                </Field>
                              </FieldGroup>
                            </FieldSet>
                          </FieldGroup>
                        </form>
                      </DialogBody>
                      <DialogFooter>
                        <Button
                          disabled={isUpdatingTalentMetrics}
                          form="board-and-operating-partners-form"
                          type="submit"
                        >
                          {isUpdatingTalentMetrics && <Spinner />}
                          Save
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                }
                title="Board & Operating Partners"
              />
              <Card>
                <div className="space-y-5">
                  <div className="border-b pb-2.5">
                    <h3 className="text-base font-medium">
                      Board of Directors
                    </h3>
                  </div>
                  <div className="grid gap-4 @md:grid-cols-2">
                    <InfoRow
                      label="Size of Board"
                      value={
                        talentMetricsData.node.talentId.sizeOfBoard?.toString() ??
                        '-'
                      }
                    />
                    <InfoRow
                      label="# of Independent Directors"
                      value={
                        talentMetricsData.node.talentId.numberOfIndependentDirectors?.toString() ??
                        '-'
                      }
                    />
                  </div>
                  <div className="grid gap-4 @md:grid-cols-2">
                    <InfoRow
                      label="Relevant Industry Experience"
                      value={
                        talentMetricsData.node.talentId
                          .hasReleventIndustryExperience === null
                          ? '-'
                          : talentMetricsData.node.talentId
                                .hasReleventIndustryExperience
                            ? 'Yes'
                            : 'No'
                      }
                    />
                    <InfoRow
                      label="# of Directors with Equity"
                      value={
                        talentMetricsData.node.talentId.numberOfDirectorsWithEquity?.toString() ??
                        '-'
                      }
                    />
                  </div>
                  <div className="border-b pb-2.5">
                    <h3 className="text-base font-medium">
                      Operating Partners
                    </h3>
                  </div>
                  <div className="space-y-2.5">
                    <OperatingPartnersTable opportunityId={id} />
                    <CommentaryField
                      label="Commentary"
                      value={
                        talentMetricsData.node.talentId.boardCommentary ?? ''
                      }
                    />
                  </div>
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </RightSidebarWrapper>
    </>
  );
};

export default InvestmentsDirectIdTalent;
